import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Download, FileText, Loader2, AlertCircle, Building2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOC_LABELS = {
  dispatch: { invoice: "Invoice", eway: "E-way bill", challan: "Delivery challan" },
  sales_invoice: { invoice: "Sales invoice" },
  vendor_invoice: { invoice: "Vendor invoice" },
  po: { invoice: "Purchase order" },
};

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
}
function fmtAmount(n) {
  if (n == null) return null;
  try { return `₹${Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 })}`; }
  catch { return String(n); }
}

export default function SharePreview() {
  const { code } = useParams();
  const [meta, setMeta] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/share-code/${code}`);
        setMeta(data);
      } catch (e) {
        setErr(e.response?.data?.detail || "Could not load this share link.");
      } finally { setLoading(false); }
    })();
  }, [code]);

  const label = meta
    ? (DOC_LABELS[meta.doc_type] || {})[meta.kind || "invoice"] || "Document"
    : "Document";

  const d = meta?.details || {};
  const download = () => window.open(`${API}/public/share-code/${code}/pdf`, "_blank");

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-slate-50 flex items-center justify-center p-6" data-testid="share-preview">
      <div className="w-full max-w-lg rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl shadow-2xl p-6 sm:p-8 space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-orange-500 to-pink-500 flex items-center justify-center">
            <FileText className="w-6 h-6 text-white" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-orange-300">TileSphere document</div>
            <div className="font-display text-xl sm:text-2xl font-black">{label}</div>
          </div>
        </div>

        {loading && (
          <div className="flex items-center gap-2 text-slate-300 py-8 justify-center">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading document…
          </div>
        )}

        {err && !loading && (
          <div className="rounded-lg bg-red-500/10 border border-red-500/30 p-4 text-sm text-red-200 flex items-start gap-2" data-testid="share-error">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>{err}</span>
          </div>
        )}

        {meta && !err && (
          <>
            <div className="rounded-xl bg-black/40 border border-white/10 divide-y divide-white/5" data-testid="share-details">
              {d.issuer && (
                <div className="px-4 py-3 flex items-center gap-2 text-sm">
                  <Building2 className="w-4 h-4 text-slate-400" />
                  <span className="text-slate-400">From</span>
                  <span className="ml-auto font-semibold">{d.issuer}</span>
                </div>
              )}
              {d.number && (
                <div className="px-4 py-3 flex items-center text-sm">
                  <span className="text-slate-400">{label} #</span>
                  <span className="ml-auto font-mono font-bold text-white">{d.number}</span>
                </div>
              )}
              {(d.customer_name || d.vendor_name) && (
                <div className="px-4 py-3 flex items-center text-sm">
                  <span className="text-slate-400">{d.customer_name ? "Customer" : "Vendor"}</span>
                  <span className="ml-auto font-semibold">{d.customer_name || d.vendor_name}</span>
                </div>
              )}
              {d.date && (
                <div className="px-4 py-3 flex items-center text-sm">
                  <span className="text-slate-400">Date</span>
                  <span className="ml-auto">{fmtDate(d.date)}</span>
                </div>
              )}
              {d.amount != null && (
                <div className="px-4 py-3 flex items-center text-sm">
                  <span className="text-slate-400">Amount</span>
                  <span className="ml-auto font-display text-lg font-black text-orange-300">{fmtAmount(d.amount)}</span>
                </div>
              )}
              {d.status && (
                <div className="px-4 py-3 flex items-center text-sm">
                  <span className="text-slate-400">Status</span>
                  <span className="ml-auto"><Badge variant="outline" className="font-mono text-[10px] border-emerald-400/40 text-emerald-300">{d.status}</Badge></span>
                </div>
              )}
            </div>

            <Button
              onClick={download}
              className="w-full text-base font-bold bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white h-12"
              data-testid="share-download-btn"
            >
              <Download className="w-5 h-5 mr-2" /> Download PDF
            </Button>

            <div className="text-[10px] text-center text-slate-500">
              Link expires {fmtDate(meta.expires_at)} · Secured by TileSphere
            </div>
          </>
        )}
      </div>
    </div>
  );
}
